import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import api, { homeCardsAPI } from '../services/api';
import { buildLanguageOptions, buildLanguageMap } from '../utils/languageConfig';
import HomeCardTranslationPreview from '../components/HomeCardTranslationPreview';
import './HomeCardManagement.css';

const HomeCardManagement = () => {
  // State management
  const [homeCards, setHomeCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    text_content: '',
    image_url: null
  });
  const [iconUploading, setIconUploading] = useState(false);

  // Translation state
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSavingTranslations, setIsSavingTranslations] = useState(false);
  const [translationProgress, setTranslationProgress] = useState({
    step: 'initializing',
    progress: 0,
    currentLanguage: '',
    completedLanguages: 0
  });
  const [translatedData, setTranslatedData] = useState(null);
  const [showTranslationPreview, setShowTranslationPreview] = useState(false);
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [translationError, setTranslationError] = useState('');

  // Tenant-supported languages (ISO codes) — drives the translate UI, matching
  // the attraction translation flow. Defaults to English until config loads.
  const [supportedLanguages, setSupportedLanguages] = useState(['en']);
  const languageOptions = buildLanguageOptions(supportedLanguages);
  const languageMap = buildLanguageMap(supportedLanguages);

  // Safe date formatter — tolerates missing/invalid timestamps (no "Invalid Date").
  // Accepts snake_case (API contract) and camelCase (defensive) keys.
  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString();
  };

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCards, setFilteredCards] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCards, setTotalCards] = useState(0);

  // Delete confirmation state
  const [deletingCards, setDeletingCards] = useState(new Set());

  // Fetch home cards
  const fetchHomeCards = async () => {
    try {
      setLoading(true);
      const response = await homeCardsAPI.getAll({
        page: currentPage,
        per_page: 10
      });
      
      if (response.success) {
        setHomeCards(response.data.cards || []);
        setTotalPages(response.data.pagination?.total_pages || 1);
        setTotalCards(response.data.pagination?.total || 0);
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch home cards: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setFilteredCards([]);
      return;
    }

    try {
      const response = await homeCardsAPI.search(searchQuery);
      if (response.success) {
        setFilteredCards(response.data.cards || []);
      }
    } catch (err) {
      setError('Search failed: ' + (err.response?.data?.message || err.message));
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setFilteredCards([]);
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Client-side validation
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only PNG, JPEG, and WebP are allowed.');
      e.target.value = '';
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      setError('File too large. Maximum size is 1 MB.');
      e.target.value = '';
      return;
    }

    try {
      setIconUploading(true);
      const result = await homeCardsAPI.uploadIcon(file);
      if (result.success) {
        setFormData(prev => ({ ...prev, image_url: result.data.url }));
        setSuccess('Icon uploaded successfully!');
      } else {
        setError(result.message || 'Icon upload failed');
      }
    } catch (err) {
      setError('Icon upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setIconUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveIcon = () => {
    setFormData(prev => ({ ...prev, image_url: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.text_content.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      if (editingCard) {
        // Update existing card
        await homeCardsAPI.update(editingCard.id, formData);
        setSuccess('Home card updated successfully!');
      } else {
        // Create new card
        await homeCardsAPI.create(formData);
        setSuccess('Home card created successfully!');
      }
      
      // Reset form and refresh data
      resetForm();
      fetchHomeCards();
      setError(null);
    } catch (err) {
      setError(editingCard 
        ? 'Failed to update home card: ' + (err.response?.data?.message || err.message)
        : 'Failed to create home card: ' + (err.response?.data?.message || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      text_content: '',
      image_url: null
    });
    setEditingCard(null);
    setShowForm(false);
    setTranslatedData(null);
    setShowTranslationPreview(false);
    setTranslationError('');
  };

  // Translation functions
  const handleTranslate = async () => {
    if (!formData.title.trim() || !formData.text_content.trim()) {
      setTranslationError('Please fill in title and content before translating');
      return;
    }

    setIsTranslating(true);
    setTranslationError('');
    setTranslatedData(null);
    setTranslationProgress({ step: 'translating', progress: 20, currentLanguage: '', completedLanguages: 0 });

    try {
      // Translate via the home-card translate endpoint. Target languages are
      // derived from tenant config server-side (no client-side Melaka list).
      const result = await homeCardsAPI.translate({
        cardId: editingCard?.id || undefined,
        title: formData.title,
        text_content: formData.text_content
      });

      if (result.success) {
        // Server returns ISO-keyed translations, e.g. { fi: { title, text_content } }.
        // Merge in the source-language base so the preview can show it too.
        const merged = {
          [sourceLanguage]: { title: formData.title, text_content: formData.text_content },
          ...(result.data?.translatedData || {})
        };
        setTranslatedData(merged);
        setTranslationProgress({ step: 'completed', progress: 100, currentLanguage: '', completedLanguages: 0 });
        setShowTranslationPreview(true);
        setSuccess('Translation completed successfully!');
      } else {
        setTranslationError(result.message || 'Translation failed');
      }
    } catch (error) {
      console.error('🌐 Translation error:', error);
      setTranslationError('Translation failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsTranslating(false);
    }
  };

  // Build the tenant `translations` JSON column from ISO-keyed translation data.
  // Excludes the source/base language — that lives in title/text_content.
  const buildTranslationsJson = (data) => {
    const out = {};
    Object.keys(data || {}).forEach((code) => {
      if (code === sourceLanguage || code === 'en') return;
      const entry = data[code] || {};
      const langObj = {};
      if (entry.title) langObj.title = entry.title;
      if (entry.text_content) langObj.text_content = entry.text_content;
      if (Object.keys(langObj).length) out[code] = langObj;
    });
    return out;
  };

  const handleSaveWithTranslation = async () => {
    if (!translatedData) {
      setError('No translation data available');
      return;
    }

    try {
      setLoading(true);

      const cardData = {
        title: formData.title,
        text_content: formData.text_content,
        image_url: formData.image_url || null,
        translations: buildTranslationsJson(translatedData)
      };

      if (editingCard) {
        await homeCardsAPI.update(editingCard.id, cardData);
        setSuccess('Home card updated with translations successfully!');
      } else {
        await homeCardsAPI.create(cardData);
        setSuccess('Home card created with translations successfully!');
      }

      resetForm();
      fetchHomeCards();
      setError(null);
    } catch (err) {
      setError(editingCard
        ? 'Failed to update home card: ' + (err.response?.data?.message || err.message)
        : 'Failed to create home card: ' + (err.response?.data?.message || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  // Save translations from preview modal
  const handleSaveTranslations = async (updatedTranslatedData) => {
    if (!updatedTranslatedData) {
      setError('No translation data to save');
      return;
    }

    // If editing an existing card, save to database
    if (editingCard && editingCard.id) {
      try {
        setIsSavingTranslations(true);
        setError(null);

        // Write the tenant `translations` JSON column (REPLACES existing
        // translations). Base/source language stays in title/text_content.
        const baseEntry = updatedTranslatedData[sourceLanguage] || {};
        const updateData = {
          translations: buildTranslationsJson(updatedTranslatedData)
        };
        if (baseEntry.title) updateData.title = baseEntry.title;
        if (baseEntry.text_content) updateData.text_content = baseEntry.text_content;

        // Update the card with corrected translations - this REPLACES existing translations
        const response = await homeCardsAPI.update(editingCard.id, updateData);
        
        console.log('✅ Translations saved successfully:', response);
        
        // Update local state
        setTranslatedData(updatedTranslatedData);
        
        // Show success message first
        setSuccess('Translations saved successfully to database!');
        
        // Refresh the cards list to show updated data
        await fetchHomeCards();
        
        // Close modal after a brief delay to show success message
        setTimeout(() => {
          setShowTranslationPreview(false);
        }, 1000);
      } catch (err) {
        setError('Failed to save translations: ' + (err.response?.data?.message || err.message));
        console.error('Error saving translations:', err);
      } finally {
        setIsSavingTranslations(false);
      }
    } else {
      // For new cards (not yet saved), just update local state
      // User will need to save the card with translations using "Save with Translations" button
      setTranslatedData(updatedTranslatedData);
      setShowTranslationPreview(false);
      setSuccess('Translations updated! Please save the card to persist changes.');
    }
  };

  const handleEdit = (card) => {
    setFormData({
      title: card.title,
      text_content: card.text_content,
      image_url: card.image_url || null
    });
    setEditingCard(card);
    setShowForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleDelete = async (card) => {
    if (deletingCards.has(card.id)) {
      return; // Prevent multiple deletions
    }
    
    if (window.confirm(`Are you sure you want to delete "${card.title}"?`)) {
      try {
        setDeletingCards(prev => new Set([...prev, card.id]));
        
        await homeCardsAPI.delete(card.id);
        setSuccess('Home card deleted successfully!');
        fetchHomeCards();
        setError(null);
      } catch (err) {
        // If card is already deleted (404), treat it as success
        if (err.response?.status === 404) {
          console.log('Card already deleted, refreshing list...');
          fetchHomeCards();
          setError(null);
        } else {
          setError('Failed to delete home card: ' + (err.response?.data?.message || err.message));
        }
      } finally {
        setDeletingCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(card.id);
          return newSet;
        });
      }
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Effects
  useEffect(() => {
    fetchHomeCards();
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load tenant-supported languages (config-driven, matches attraction UI)
  useEffect(() => {
    const fetchTenantLanguages = async () => {
      try {
        const response = await api.get('/api/v1/config');
        const codes = response.data?.data?.supportedLanguages;
        if (Array.isArray(codes) && codes.length > 0) {
          setSupportedLanguages(codes);
          if (!codes.includes(sourceLanguage)) setSourceLanguage(codes[0]);
        }
      } catch (err) {
        console.warn('Failed to load tenant languages, defaulting to English:', err.message);
      }
    };
    fetchTenantLanguages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchQuery.trim()) {
      const debounceTimer = setTimeout(() => {
        handleSearch();
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      setFilteredCards([]);
    }
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const displayCards = filteredCards.length > 0 ? filteredCards : homeCards;

  return (
    <Layout title="Home Card Management">
      <div className="home-card-management">
      <div className="page-header">
        <div className="header-content">
          <h1>🏠 Home Card Management</h1>
          <p>Manage home page content cards</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
            disabled={loading}
          >
            <span className="btn-icon">➕</span>
            Add New Card
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">❌</span>
          {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search home cards by title or content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={clearSearch}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {filteredCards.length > 0 && (
          <div className="search-results-info">
            Found {filteredCards.length} card{filteredCards.length !== 1 ? 's' : ''} matching "{searchQuery}"
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingCard ? 'Edit Home Card' : 'Create New Home Card'}</h2>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="card-form">
              <div className="form-group">
                <label htmlFor="title">Card Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter card title (e.g., Welcome to Travel Futura)"
                  maxLength="255"
                  required
                />
                <div className="char-count">{formData.title.length}/255</div>
              </div>

              <div className="form-group">
                <label htmlFor="text_content">Card Content *</label>
                <textarea
                  id="text_content"
                  name="text_content"
                  value={formData.text_content}
                  onChange={handleInputChange}
                  placeholder="Enter the main content for this card..."
                  rows="6"
                  maxLength="10000"
                  required
                />
                <div className="char-count">{formData.text_content.length}/10000</div>
              </div>

              {/* Icon Upload Section */}
              <div className="form-group">
                <label>Card Icon (optional)</label>
                {formData.image_url ? (
                  <div className="icon-preview" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                    <img
                      src={formData.image_url}
                      alt="Card icon"
                      style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={handleRemoveIcon}
                    >
                      Remove
                    </button>
                    <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer', margin: 0 }}>
                      Replace
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleIconUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                ) : (
                  <div style={{ marginTop: '8px' }}>
                    <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer' }}>
                      {iconUploading ? 'Uploading...' : 'Upload Icon'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleIconUpload}
                        disabled={iconUploading}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                      PNG, JPEG, or WebP. Max 1 MB.
                    </small>
                  </div>
                )}
              </div>

              {/* Translation Section */}
              <div className="translation-section">
                <div className="translation-header">
                  <h3>🌐 Translation</h3>
                  <div className="source-language-selector">
                    <label htmlFor="sourceLanguage">Source Language:</label>
                    <select
                      id="sourceLanguage"
                      value={sourceLanguage}
                      onChange={(e) => setSourceLanguage(e.target.value)}
                      disabled={isTranslating}
                    >
                      {languageOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {translationError && (
                  <div className="alert alert-error">
                    <span className="alert-icon">❌</span>
                    {translationError}
                  </div>
                )}

                <div className="translation-actions">
                  <button
                    type="button"
                    className="btn btn-translate"
                    onClick={handleTranslate}
                    disabled={isTranslating || !formData.title.trim() || !formData.text_content.trim()}
                  >
                    {isTranslating ? '⏳ Translating...' : '🌐 Translate to All Languages'}
                  </button>

                  {translatedData && (
                    <button
                      type="button"
                      className="btn btn-preview"
                      onClick={() => setShowTranslationPreview(true)}
                    >
                      👁️ Preview Translations
                    </button>
                  )}
                </div>

                {isTranslating && (
                  <div className="translation-progress">
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                    </div>
                    <div className="progress-text">
                      {translationProgress.step === 'initializing' && 'Initializing translation...'}
                      {translationProgress.step === 'translating' && `Translating to ${translationProgress.currentLanguage}...`}
                      {translationProgress.step === 'completed' && 'Translation completed!'}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? '⏳ Saving...' : (editingCard ? '✏️ Update Card' : '➕ Create Card')}
                </button>
                {translatedData && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleSaveWithTranslation}
                    disabled={loading}
                  >
                    {loading ? '⏳ Saving...' : '🌐 Save with Translations'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Translation Preview Modal */}
      {showTranslationPreview && translatedData && (
        <div className="modal-overlay">
          <div className="modal-content translation-preview-modal">
            <div className="modal-header">
              <h2>🌐 Translation Preview</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowTranslationPreview(false)}
              >
                ×
              </button>
            </div>
            
            <HomeCardTranslationPreview
              translatedData={translatedData}
              sourceLanguage={sourceLanguage}
              supportedLanguages={languageMap}
              onClose={() => setShowTranslationPreview(false)}
              onSave={handleSaveTranslations}
              loading={isSavingTranslations}
            />
          </div>
        </div>
      )}

      {/* Cards List */}
      <div className="cards-section">
        <div className="section-header">
          <h2>
            Home Cards ({totalCards} total)
            {filteredCards.length > 0 && (
              <span className="search-indicator"> - Search Results</span>
            )}
          </h2>
        </div>

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading home cards...</p>
          </div>
        )}

        {!loading && displayCards.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🏠</div>
            <h3>{searchQuery ? 'No cards found' : 'No home cards yet'}</h3>
            <p>
              {searchQuery 
                ? `No home cards match "${searchQuery}". Try a different search term.`
                : 'Get started by creating your first home card.'
              }
            </p>
            {!searchQuery && (
              <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                Create First Card
              </button>
            )}
          </div>
        )}

        {!loading && displayCards.length > 0 && (
          <div className="cards-grid">
            {displayCards.map((card) => (
              <div key={card.id} className="card-item">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                    {card.image_url && (
                      <img
                        src={card.image_url}
                        alt=""
                        style={{ width: '32px', height: '32px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }}
                      />
                    )}
                    <h3 className="card-title" style={{ margin: 0 }}>{card.title}</h3>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(card)}
                      title="Edit card"
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(card)}
                      disabled={deletingCards.has(card.id)}
                      title={deletingCards.has(card.id) ? "Deleting..." : "Delete card"}
                    >
                      {deletingCards.has(card.id) ? '⏳' : '🗑️'}
                    </button>
                  </div>
                </div>
                
                <div className="card-content">
                  <p>{card.text_content}</p>
                </div>
                
                <div className="card-footer">
                  <div className="card-meta">
                    <small>
                      {(() => {
                        const created = formatDate(card.created_at || card.createdAt);
                        const updated = formatDate(card.updated_at || card.updatedAt);
                        return (
                          <>
                            Created: {created || 'N/A'}
                            {updated && updated !== created && (
                              <> • Updated: {updated}</>
                            )}
                          </>
                        );
                      })()}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && !filteredCards.length && totalPages > 1 && (
          <div className="pagination">
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              ← Previous
            </button>
            
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
      </div>
    </Layout>
  );
};

export default HomeCardManagement;
